import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box, Card, CardContent, Tab, TextField, MenuItem, CircularProgress,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PLANTS_NAME } from '../../../../constants/graphs';
import { GetSectorPlantConfigResponse } from '../../../../requests/api/apiTypes';
import ConversationsTab from '../../ConversationTab';
import useStyles from '../../styles';
import apiClient from '../../../../requests/api/apiClient';
import useFetchApi from '../../../../hooks/useFetchApi';
import { FetchError } from '../../../../views/types';
import { ModalState, EMPTY_MODAL_STATE } from '../../chartModal';
import useIsClientOperator from '../../../../hooks/useIsClientOperator';

const PlantsConfigContent = ({
  sectorId,
  setModalState: setModalStateProp,
  refreshCounter = 0,
  onConfigSaved,
}: {
  sectorId: string;
  setModalState?: React.Dispatch<React.SetStateAction<ModalState>>;
  refreshCounter?: number;
  onConfigSaved?: () => void;
}) => {
  const classes = useStyles();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const { enqueueSnackbar } = useSnackbar();

  // Default to the conversations tab when arriving from a notification (date or source param).
  const date = queryParams.get('date');
  const source = queryParams.get('source');
  const initialTabValue = date || source === 'notification' ? '3' : '1';

  const [tabValue, setTabValue] = useState(initialTabValue);
  const [isLoading, setIsLoading] = useState(true);
  const [measureBy, setMeasureBy] = useState('');

  const [, setInternalModalState] = useState<ModalState>(EMPTY_MODAL_STATE);
  const setModalState = setModalStateProp ?? setInternalModalState;

  const isClientOperator = useIsClientOperator();

  useEffect(() => {
    if (isClientOperator && tabValue === '1') {
      setTabValue('2');
    }
  }, [isClientOperator, tabValue]);

  const { data, loading } = useFetchApi<GetSectorPlantConfigResponse>({
    modelEndpoint: 'plantConfigDetail',
    modelName: 'plantConfig',
    id: sectorId,
    dependencies: [sectorId],
  });

  useEffect(() => {
    if (!loading) {
      setMeasureBy(data?.plantConfig?.measureBy || 'Tronco');
      setIsLoading(false);
    }
  }, [data, loading]);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  }, []);

  const handleSortByChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setMeasureBy(newValue);
    try {
      const { data: response } = await apiClient.plantConfig.plantConfigCreate(
        sectorId,
        { measureBy: newValue },
      );
      enqueueSnackbar(response.message, { variant: 'success' });
      // measureBy changes what sensor data is displayed — notify chart to refresh.
      onConfigSaved?.();
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  }, [sectorId, enqueueSnackbar, onConfigSaved]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TabContext value={tabValue}>
      <Box sx={{ borderBottom: 0 }}>
        <TabList onChange={handleTabChange} aria-label="sector tabs">
          {!isClientOperator && (
            <Tab label="Configuración" value="1" className={classes.tabLabel} />
          )}
          <Tab label="Alertas" value="2" className={classes.tabLabel} />
          <Tab label="Conversaciones" value="3" className={classes.tabLabel} />
        </TabList>
      </Box>
      {!isClientOperator && (
        <TabPanel value="1" className={classes.configTabPanel}>
          <Card className={classes.configCard}>
            <CardContent>
              <Box className={classes.selectFieldsContainer}>
                <TextField
                  label="Elemento medido"
                  variant="standard"
                  value={measureBy}
                  onChange={handleSortByChange}
                  select
                  className={classes.selectField}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="Tronco">Tronco</MenuItem>
                  <MenuItem value="Tallo">Tallo</MenuItem>
                  <MenuItem value="Brote">Brote</MenuItem>
                </TextField>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>
      )}
      <TabPanel value="2" className={classes.configTabPanel}>
        In progress
      </TabPanel>
      <TabPanel value="3" className={classes.configTabPanel}>
        <ConversationsTab
          setModalState={setModalState}
          sectorId={sectorId}
          chartName={PLANTS_NAME}
          refreshCounter={refreshCounter}
        />
      </TabPanel>
    </TabContext>
  );
};

export default PlantsConfigContent;
